import { API_URLS } from "@solomon-apps/config";
import { useQuery } from "@tanstack/react-query";

const RESTRICTED_COUNTRIES = ["US", "CU", "IR", "KP", "SY", "RU", "BY", "SD", "MM", "AF", "IQ"];

interface GeoInfoResponse {
    country: string;
    ip?: string;
    countryName?: string;
}

export interface GeoRestrictionResult {
    isRestricted: boolean;
    isChecked: boolean;
    ip: string | undefined;
    country: string | undefined;
}

const COUNTRY_NAMES: Record<string, string> = {
    US: "United States",
    CU: "Cuba",
    IR: "Iran",
    KP: "North Korea",
    SY: "Syria",
    RU: "Russia",
    BY: "Belarus",
    SD: "Sudan",
    MM: "Myanmar",
    AF: "Afghanistan",
    IQ: "Iraq"
};

export function useGeoRestriction(): GeoRestrictionResult {
    const query = useQuery<GeoInfoResponse>({
        queryKey: ["geo-info"],
        queryFn: async ({ signal }) => {
            const response = await fetch(`${API_URLS.REWARDS}/info`, { signal });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            return response.json() as Promise<GeoInfoResponse>;
        },
        staleTime: 30 * 60 * 1000,
        retry: 1,
        refetchOnWindowFocus: true,
        refetchOnReconnect: false
    });

    if (query.isError) {
        console.warn("[Geo] Failed to fetch location info, blocking access:", query.error);
    }

    const isRestricted = query.isError ? true : query.data ? RESTRICTED_COUNTRIES.includes(query.data.country) : false;
    const countryCode = query.data?.country;

    return {
        isRestricted,
        isChecked: query.isSuccess || query.isError,
        ip: query.data?.ip,
        country: query.data?.countryName ?? (countryCode ? COUNTRY_NAMES[countryCode] : undefined)
    };
}
